import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const contactAlt = (form.querySelector('[name="contact_alt"]') as HTMLInputElement)?.value?.trim();
    if (contactAlt) {
      setSubmitted(true);
      return;
    }
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d8tp18r9b1tpbugifsmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (_) {
      // silently handle
    }
    setSubmitting(false);
    setSubmitted(true);
    setEmail('');
  };

  return (
    <footer className="bg-foreground-950 rounded-2xl mx-4 md:mx-6 mb-4 md:mb-6 mt-8 overflow-hidden">
      <div className="px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          <div>
            <h4 className="text-background-50 text-xl md:text-2xl font-heading font-bold mb-3 whitespace-nowrap">Get Updates</h4>
            <p className="text-foreground-300 text-sm leading-relaxed mb-5 max-w-xs">
              Subscribe to receive the latest AcBuy Spreadsheet updates, new product finds, and shopping guides straight to your inbox.
            </p>
            {submitted ? (
              <p className="text-accent-400 text-sm font-medium whitespace-nowrap">
                <i className="ri-check-line mr-1"></i>You are subscribed!
              </p>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col sm:flex-row gap-2 max-w-xs">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="contact_alt_field"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 bg-transparent border-b border-foreground-600 text-background-50 text-sm py-2 px-0 placeholder-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-background-50 hover:bg-background-100 text-foreground-950 text-sm font-semibold rounded-full whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : 'Subscribe'}
                </button>
              </form>
            )}
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h5 className="text-background-50 text-sm font-semibold mb-4 uppercase tracking-wider whitespace-nowrap">Quick Links</h5>
              <div className="flex flex-col gap-2.5">
                <Link to="/" className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap">Home</Link>
                <Link to={`/guide${articles[0].slug}`} className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap">Spreadsheet Guide</Link>
                {categories.slice(0, 4).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-background-50 text-sm font-semibold mb-4 uppercase tracking-wider whitespace-nowrap">Resources</h5>
              <div className="flex flex-col gap-2.5">
                {categories.slice(4, 8).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Main Site →
                </a>
              </div>
            </div>
          </div>

          <div>
            <h5 className="text-background-50 text-sm font-semibold mb-4 uppercase tracking-wider whitespace-nowrap">Legal</h5>
            <div className="flex flex-col gap-2.5">
              <a href="#" className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="#" className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap">Terms of Service</a>
              <a href="#" className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap">Disclaimer</a>
              <a href="#" className="text-foreground-300 hover:text-background-50 text-sm transition-colors whitespace-nowrap">Contact</a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <a href="#" className="text-foreground-400 hover:text-background-50 transition-colors cursor-pointer">
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a href="#" className="text-foreground-400 hover:text-background-50 transition-colors cursor-pointer">
              <i className="ri-twitter-x-line text-lg"></i>
            </a>
            <a href="#" className="text-foreground-400 hover:text-background-50 transition-colors cursor-pointer">
              <i className="ri-reddit-line text-lg"></i>
            </a>
          </div>
          <p className="text-foreground-500 text-xs whitespace-nowrap">
            &copy; {new Date().getFullYear()} AcBuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a href="#" className="text-foreground-400 hover:text-background-50 text-xs transition-colors whitespace-nowrap">Privacy Policy</a>
            <a href="#" className="text-foreground-400 hover:text-background-50 text-xs transition-colors whitespace-nowrap">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}